import { CommonModule } from '@angular/common';
import {
  Component,
  input,
  output,
  signal,
  computed,
  inject,
  OnInit,
  DestroyRef,
  forwardRef,
} from '@angular/core';
import { ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, NavigationEnd } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';
import { filter, fromEvent } from 'rxjs';

import { VOneButtonComponent, VOneIconComponent } from '@shared-components';
import { IconType } from '@shared-enums';
import { SidebarItem } from '@shared-layouts';
import { LanguageSelectorComponent } from '@shared-layouts';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    VOneIconComponent,
    VOneButtonComponent,
    TranslateModule,
    forwardRef(() => LanguageSelectorComponent),
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  items = input.required<SidebarItem[]>();
  isCollapsed = input<boolean>(false);

  collapsedChange = output<boolean>();
  itemClick = output<SidebarItem>();

  readonly expandedItems = signal<Set<string>>(new Set());
  readonly ICON_TYPE = IconType;
  readonly currentRoute = signal<string>('');
  readonly collapsedState = computed(() => this.isCollapsed());

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.setupEscapeKeyHandler();
  }

  ngOnInit(): void {
    this.currentRoute.set(this.router.url);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.url);
      });
  }

  private setupEscapeKeyHandler(): void {
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter((event) => event.key === 'Escape'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.collapsedChange.emit(true);
      });
  }

  toggleSidebar(): void {
    const newState = !this.isCollapsed();
    this.collapsedChange.emit(newState);

    if (newState) {
      this.expandedItems.set(new Set());
    }
  }

  toggleExpanded(itemId: string): void {
    const expanded = this.expandedItems();
    const newExpanded = new Set(expanded);

    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }

    this.expandedItems.set(newExpanded);
  }

  toggleItem(item: SidebarItem): void {
    if (!item.children || item.children.length === 0) {
      this.navigateToItem(item);
      return;
    }

    const expanded = this.expandedItems();
    const newExpanded = new Set(expanded);

    if (newExpanded.has(item.id)) {
      newExpanded.delete(item.id);
    } else {
      newExpanded.add(item.id);
    }

    this.expandedItems.set(newExpanded);
  }

  navigateToItem(item: SidebarItem): void {
    if (item.route) {
      this.currentRoute.set(item.route);
      this.router.navigate([item.route]);
    }

    if (item.action) {
      item.action();
    }

    this.itemClick.emit(item);

    this.collapsedChange.emit(true);
  }

  isItemExpanded(itemId: string): boolean {
    return this.expandedItems().has(itemId);
  }

  isItemActive(item: SidebarItem): boolean {
    if (item.route) {
      return (
        this.currentRoute() === item.route ||
        this.currentRoute().startsWith(item.route + '/')
      );
    }
    return false;
  }

  hasChildren(item: SidebarItem): boolean {
    return !!(item.children && item.children.length > 0);
  }
}
